import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react'
import { useVenti } from 'venti'
import {
	ApplicationRowData,
	SiteConfiguration,
} from '@matech/thebigpos-sdk'
import { useQuery } from '@tanstack/react-query'
import userAvatar from '@/assets/images/landingPages/user-avatar.png'
import { Roles } from '../../services/client'
import useUser from '../../hooks/useUser'
import { getTheme } from '../../config'
import { useAppContext } from '../AppContext'
import queryKeys from '../../services/queryKeys'
import { TheBigPOSApi } from '../../lib/TheBigPOSClient'
import ModalLoanAppSelector from '../modals/ModalLoanAppSelector'
import { LoanData } from '../../types'
import { ModalPleaseWait } from '../modals/ModalPleaseWait'
import PortalHeaderDetail from './PortalHeaderDetail'
import PortalHeaderDetailLink from './PortalHeaderDetailLink'
import { Button } from '../Button'
import { LayoutRow } from '../layout'
import { useLoanContext } from '../../contexts/LoanContext'
import { getTitleByEntityType } from '../../services/utils'

const theme = getTheme()

const PortalHeader = () => {
	const { user, isAdmin } = useUser()
	const ventiState = useVenti()
	const { state } = useAppContext()
	const [loanIdModalVisible, setLoanIdModalVisible] = useState(false)
	const {
		loanId,
		loanData: legacyLoan,
		isRefetching,
		isLoading,
		setActiveLoanId,
	} = useLoanContext()

	const { siteConfig, impersonationRequest } = state as {
		siteConfig: SiteConfiguration | null
		impersonationRequest: { status: string } | null
	}

	const isFirstLoad = !ventiState.get(theme.storageKeys.isFirstLoad)
	const appPosting = ventiState.get(theme.storageKeys.appPosting)
	const appPosted = ventiState.get(theme.storageKeys.appPosted)

	const isBorrower = [Roles.borrower].includes(user.role)
	const isImpersonator =
		loanId &&
		[
			Roles.loanOfficer,
			Roles.branchManager,
			Roles.settlementAgent,
			Roles.realtor,
			Roles.loanOfficerAssistant,
			Roles.loanProcessor,
		].includes(user.role)

	const stopImpersonation = useCallback(() => {
		setActiveLoanId('')
		ventiState.set(theme.storageKeys.loanDocs, null)
	}, [setActiveLoanId])

	const isLoansQueryEnabled = useMemo(() => {
		return (
			user?.isLoggedIn &&
			[
				Roles.loanOfficer,
				Roles.branchManager,
				Roles.borrower,
			].includes(user.role)
		)
	}, [user?.isLoggedIn, user?.role])

	const {
		data: loans,
		isError: isLoansError,
		isFetching: isFetchingLoans,
	} = useQuery({
		queryKey: [queryKeys.loans],
		queryFn: () => TheBigPOSApi.getLoans(),
		enabled: isLoansQueryEnabled,
		refetchOnWindowFocus: false,
	})

	const { data: tasksDocumentsByLoan } = useQuery({
		queryKey: [queryKeys.loanDocuments, loanId],
		queryFn: () => TheBigPOSApi.getTaskDocumentsByLoan(loanId),
		enabled: !!loanId,
		refetchOnWindowFocus: false,
	})

	const legacyLoanData: LoanData = useMemo(() => {
		if (!legacyLoan) return []

		const newLegacyLoanData = legacyLoan
		const subjectPropertyAddress = []

		subjectPropertyAddress.push(
			newLegacyLoanData.SubjectPropertyAddressStreet !== ''
				? (newLegacyLoanData.SubjectPropertyAddressStreet ?? 'TBD')
				: 'TBD'
		)

		if (newLegacyLoanData.SubjectPropertyAddressUnitNumber) {
			subjectPropertyAddress.push(
				`${newLegacyLoanData.SubjectPropertyAddressUnitType} ${newLegacyLoanData.SubjectPropertyAddressUnitNumber}`
			)
		}

		if (newLegacyLoanData.SubjectPropertyAddressCity) {
			subjectPropertyAddress.push(
				`${newLegacyLoanData.SubjectPropertyAddressCity}, ${newLegacyLoanData.SubjectPropertyAddressState} ${newLegacyLoanData.SubjectPropertyAddressZip}`
			)
		}

		if (subjectPropertyAddress.length > 0) {
			return {
				...newLegacyLoanData,
				subjectPropertyAddress,
			}
		}
		return newLegacyLoanData
	}, [legacyLoan])

	if (isLoansError) {
		ventiState.set(
			theme.storageKeys.errorMessage,
			'There was a problem fetching your account details. Please try again later.'
		)
	}

	const loanApplications = loans?.data?.applications || []

	const loanOfficer =
		legacyLoanData?.loanOfficerSiteConfiguration ?? {}

	const slideLogoUri = useMemo(() => {
		if (user.role === Roles.loanOfficer) {
			return userAvatar
		}

		if ([Roles.borrower, Roles.superAdmin].includes(user.role)) {
			return (
				loanOfficer.profilePhotoUrl ??
				siteConfig?.profilePhotoUrl ??
				siteConfig?.portalLogoUrl
			)
		}
		return false
	}, [
		loanOfficer.profilePhotoUrl,
		siteConfig?.portalLogoUrl,
		siteConfig?.profilePhotoUrl,
		user.role,
	])

	const loanDetails = [
		{
			label: 'Loan Number',
			value: legacyLoanData?.LoanNumber,
		},
		{
			label: 'Primary Borrower',
			value: `${legacyLoanData?.BorrowerFirstName ?? ''} ${legacyLoanData?.BorrowerLastName ?? ''}`,
		},
		{
			label: 'Contact Phone',
			value: legacyLoanData?.BorrowerContactPhone,
		},
		{
			label: 'Email',
			value: legacyLoanData?.BorrowerEmail,
		},
		{
			label: 'Address',
			value: legacyLoanData?.subjectPropertyAddress?.join(', '),
		},
	]

	function getBorrowersLoanDetails(
		legacyLoanData: LoanData
	): { label: string; value: string }[] {
		const loanSummary: {
			label: string
			value: string
		}[] = [
			(legacyLoanData.BorrowerFirstName ||
				legacyLoanData.BorrowerLastName) && {
				label: 'Borrower Name',
				value: `${legacyLoanData.BorrowerFirstName ?? ''} ${legacyLoanData.BorrowerLastName ?? ''}`,
			},
			legacyLoanData.LoanPurpose && {
				label: 'Purpose',
				value: legacyLoanData.LoanPurpose,
			},
			legacyLoanData.LoanPurpose === 'Purchase' && {
				label: 'Purchase Price',
				value: `$${legacyLoanData.SubjectPropertySalePrice}`,
			},
			legacyLoanData.LoanPurpose === 'Purchase' && {
				label: 'Down Payment',
				value: `$${legacyLoanData.DownPaymentAmount ?? 0}`,
			},
			legacyLoanData.TotalLoanAmount && {
				label: 'Loan Amount',
				value: `$${legacyLoanData.TotalLoanAmount}`,
			},
			{
				label: 'Term',
				value: legacyLoanData.LoanTerm
					? `${parseInt(legacyLoanData.LoanTerm, 10) / 12} Years`
					: 'Pending',
			},
			legacyLoanData.CurrentLoanType && {
				label: 'Type',
				value: legacyLoanData.CurrentLoanType,
			},
			legacyLoanData.LoanStatus && {
				label: 'Status',
				value: legacyLoanData.LoanStatus,
			},
		]

		return loanSummary.filter(Boolean)
	}

	function getTitle(): string | false {
		if (
			!loanId &&
			[Roles.loanOfficer, Roles.branchManager].includes(user.role)
		) {
			return 'Loan Officer Dashboard'
		}

		if (!loanId && [Roles.realtor].includes(user.role)) {
			return 'Agent Dashboard'
		}

		if (isAdmin) {
			return `${user.role === Roles.superAdmin ? 'Super ' : ''}Admin Dashboard`
		}

		return false
	}

	const handleOpenLoanIdModalClick = () => {
		setLoanIdModalVisible(true)
	}

	const handleSetActiveLoanApp = (
		selectedApp: ApplicationRowData
	) => {
		setActiveLoanId(selectedApp.loanID)
	}

	useEffect(() => {
		if (legacyLoanData) {
			ventiState.set(
				theme.storageKeys.loanOfficer,
				legacyLoanData.loanOfficerSiteConfiguration
			)
		}
	}, [legacyLoanData])

	useEffect(() => {
		if (tasksDocumentsByLoan?.data) {
			ventiState.set(
				theme.storageKeys.loanDocs,
				tasksDocumentsByLoan.data
			)
		}
	}, [tasksDocumentsByLoan?.data])

	useEffect(() => {
		if (
			loanApplications.length &&
			(isFirstLoad || impersonationRequest?.status === 'Active')
		) {
			ventiState.set(theme.storageKeys.isFirstLoad, true)
			if (loanApplications.length > 1) {
				if (!user?.drafts.length) setLoanIdModalVisible(true)
			} else {
				setActiveLoanId(loanApplications[0].loanID)
			}
		}
	}, [
		impersonationRequest?.status,
		isFirstLoad,
		loanApplications,
		setActiveLoanId,
		user?.drafts.length,
	])

	useEffect(() => {
		if (!legacyLoan && impersonationRequest?.status === 'Active') {
			stopImpersonation()
		}
	}, [impersonationRequest?.status, legacyLoan, stopImpersonation])

	const isModalLoanAppSelectorVisible =
		loanIdModalVisible &&
		loanApplications.length > 1 &&
		Roles.borrower === user.role &&
		!appPosting &&
		!appPosted

	return (
		<>
			<div className="w-full m-0 h-full pb-8 pt-5">
				{!isBorrower && getTitle() && (
					<>
						<h1 className="text-2xl font-bold dark:text-white px-8 pb-5">
							{getTitle()}
						</h1>
						<hr className="border-b-2" />
					</>
				)}

				<div className="px-8">
					{isBorrower && (
						<LayoutRow className="flex flex-col md:flex-row gap-6">
							{slideLogoUri && (
								<div className="hidden md:block max-w-52">
									<img src={slideLogoUri} alt="logo" />
								</div>
							)}
							<div className="md:pb-8">
								{(getTitleByEntityType(siteConfig?.entityType) ===
									'Loan Officer' ||
									loanOfficer.name) &&
									siteConfig?.userTitle && (
										<LayoutRow>
											<h1 className="text-lg font-bold dark:text-white">
												Your {siteConfig?.userTitle}
											</h1>
										</LayoutRow>
									)}
								<LayoutRow>
									<h2 className="text-2xl font-bold dark:text-white">
										{loanOfficer.name ?? siteConfig?.name}
									</h2>
									{!!(loanOfficer.nmlsid ?? siteConfig?.nmlsid) && (
										<PortalHeaderDetail
											label="NMLS #"
											value={loanOfficer.nmlsid ?? siteConfig?.nmlsid}
										/>
									)}
								</LayoutRow>
								<PortalHeaderDetail label="mobile">
									<PortalHeaderDetailLink
										href={`tel:${loanOfficer.phone ?? siteConfig?.phone}`}
										title={loanOfficer.phone ?? siteConfig?.phone}
									/>
								</PortalHeaderDetail>
								{!!siteConfig?.tollFree && (
									<PortalHeaderDetail label="office">
										<PortalHeaderDetailLink
											href={`tel:${loanOfficer.tollFree ?? siteConfig.tollFree}`}
											title={
												loanOfficer.tollFree ?? siteConfig.tollFree
											}
										/>
									</PortalHeaderDetail>
								)}
								{!!siteConfig?.fax && (
									<PortalHeaderDetail
										label="fax"
										value={siteConfig.fax}
									/>
								)}
								{!!(loanOfficer.email ?? siteConfig?.email) && (
									<PortalHeaderDetail label="email">
										<PortalHeaderDetailLink
											href={`mailto:${loanOfficer.email ?? siteConfig?.email}`}
											title={loanOfficer.email ?? siteConfig?.email}
										/>
									</PortalHeaderDetail>
								)}
							</div>
							{legacyLoanData?.TotalLoanAmount && (
								<LayoutRow className="ml-32 hidden md:block">
									<h3 className="text-lg font-bold dark:text-white">
										Your Loan Summary
									</h3>
									<LayoutRow>
										<ul>
											{getBorrowersLoanDetails(legacyLoanData).map(
												(detail) => (
													<li
														key={`portal-header__borrowers-loan-details--${detail.label}`}
													>
														<PortalHeaderDetail
															label={detail.label}
															value={detail.value}
														/>
													</li>
												)
											)}
										</ul>
									</LayoutRow>

									{loanApplications.length > 1 && (
										<Button
											onClick={handleOpenLoanIdModalClick}
											variant="text"
											className="!text-sm !p-0 !bg-transparent !font-bold hover:!underline"
											text="Work with a different application"
										/>
									)}
								</LayoutRow>
							)}
						</LayoutRow>
					)}

					{isImpersonator && (
						<div>
							<LayoutRow className="break-words">
								{isRefetching || isLoading ? (
									<p className="text-lg mt-1 text-slate-600">
										Fetching loan data...
									</p>
								) : (
									loanDetails.map((detail) => (
										<PortalHeaderDetail
											key={`portal-header-${detail.label}`}
											label={detail.label}
											value={detail.value}
										/>
									))
								)}
							</LayoutRow>
							<LayoutRow>
								<Button
									className="text-sm !bg-red-500 !text-white !font-bold"
									onClick={stopImpersonation}
									text={
										[Roles.loanOfficer, Roles.branchManager].includes(
											user.role
										)
											? 'Stop Impersonating'
											: 'Close File'
									}
								/>
							</LayoutRow>
						</div>
					)}
				</div>
			</div>
			<ModalLoanAppSelector
				visible={isModalLoanAppSelectorVisible}
				setVisible={setLoanIdModalVisible}
				siteConfig={siteConfig}
				loanApplications={loanApplications}
				setActiveLoanApp={handleSetActiveLoanApp}
			/>
			<ModalPleaseWait
				message="Retrieving account information"
				visible={isFetchingLoans && !appPosting}
			/>
		</>
	)
}

export default PortalHeader
